'use client';

import spTracker from '@smartproxy/sp-tracker-js';
import { useEffect, useState } from 'react';

import { Experiment } from '@/lib/experimentation';
import {
  COOKIE_NAMES,
  LOCAL_STORAGE_KEYS,
} from '@/lib/experimentation/constants';
import { loadExperiments } from '@/lib/experimentation/loadExperiments';

import { isProd } from '@/constant/env';
import { getCookie, setCookie } from '@/utils/cookies';

if (typeof window !== 'undefined') {
  const bootstrapExperiments = getCookie(
    COOKIE_NAMES.BOOTSTRAP_EXPERIMENTS
  ) as any;

  let bootstrapData = {};

  if (bootstrapExperiments?.distinctID && bootstrapExperiments?.featureFlags) {
    bootstrapData = {
      distinctID: bootstrapExperiments.distinctID,
      featureFlags: bootstrapExperiments.featureFlags,
    };
  }

  spTracker.init({
    project: 'web',
    isProduction: isProd,
    options: {
      bootstrap: bootstrapData,
      api_host: '/ph',
    },
    enablement: {
      isPostHogEnabled: true,
    },
  });
}

const preloadStrapiExperiments = async () => {
  if (localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS)) {
    return;
  }
  return loadExperiments(
    'https://decodo.com/cms-api/experiments?pLevel=deep'
  ).then((experiments) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.EXPERIMENTS,
      JSON.stringify(experiments)
    );
    return experiments;
  });
};

/*
 NOTE for devs:
  the tracking lib transforms the keys before usage, like:
  data.enabledFeatureFlags => data[$enabled_feature_flags]
*/

const preloadBootstrapFlags = async (): Promise<boolean> => {
  const bootstrapCookie = getCookie(COOKIE_NAMES.BOOTSTRAP_EXPERIMENTS);
  if (bootstrapCookie) {
    return false;
  }
  const posthogLocalStorageData = localStorage.getItem(
    LOCAL_STORAGE_KEYS.POSTHOG_DISTINCT_ID
  ) as any;
  if (!posthogLocalStorageData) return false;
  // console.log('[preloadBootstrapFlags] 1 - ', {
  //   posthogLocalStorageData,
  //   bootstrapCookie,
  // });
  const posthogData = JSON.parse(posthogLocalStorageData);
  const flags = posthogData?.['$enabled_feature_flags'];
  const distinctId = posthogData?.['distinct_id'];

  // console.log('[preloadBootstrapFlags] 2 -', {
  //   posthogData,
  //   flags,
  //   distinctId,
  // });

  if (!flags || !distinctId) return false;

  const experiments = Object.keys(flags).reduce((acc, key) => {
    if (key.startsWith('experiment')) {
      acc[key] = flags[key];
    }
    return acc;
  }, {} as Record<string, string>);

  const experimentKeys = Object.keys(experiments);

  // console.log('[preloadBootstrapFlags] 3 -', {
  //   experiments,
  //   experimentKeys,
  // });

  const activeExperiments = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS) || '[]'
  ) as Experiment[];

  // console.log('[preloadBootstrapFlags] 4 -', {
  //   activeExperiments,
  // });

  const featureFlagPayloads = Object.keys(
    posthogData?.['$feature_flag_payloads']
  )
    .filter((key) => experimentKeys.includes(key))
    .reduce((acc, key) => {
      acc[key] = JSON.parse(posthogData?.['$feature_flag_payloads'][key]);

      return acc;
    }, {} as Record<string, unknown>);

  // console.log('[preloadBootstrapFlags] 5 - setting this as cookie', {
  //   distinctID: distinctId,
  //   featureFlags: experiments,
  //   preloaded: true,
  //   experimentPaths: Object.fromEntries(
  //     activeExperiments.map((exp) => [exp.controlPath, exp.flagId])
  //   ),
  //   featureFlagPayloads,
  // });

  setCookie({
    name: COOKIE_NAMES.BOOTSTRAP_EXPERIMENTS,
    value: JSON.stringify({
      distinctID: distinctId,
      featureFlags: experiments,
      preloaded: true,
      experimentPaths: Object.fromEntries(
        activeExperiments.map((exp) => [exp.controlPath, exp.flagId])
      ),
      featureFlagPayloads,
    }),
    domain: null,
  });

  return true;
};

export function PHProvider({ children }: { children: React.ReactNode }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (loaded) return;

    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS)) {
      preloadStrapiExperiments();
      // console.log('[preloadBootstrapFlags] 3.1 -', {
      //   experiments,
      // });
    }
    const intId = setInterval(async () => {
      const done = await preloadBootstrapFlags();
      if (done) {
        setLoaded(true);
        clearInterval(intId);
      }
    }, 1000);

    return () => {
      clearInterval(intId);
    };
  }, [loaded]);
  return <>{children}</>;
}
